import React, { useRef, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import ErrorIcon from "@mui/icons-material/Error";
import FileUploadIcon from "@mui/icons-material/UploadFile";
import PublishIcon from "@mui/icons-material/Publish";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import { importDevices } from "./deviceImportApi";
import { readImportFile } from "./deviceImportFile";
import {
  applyPaste,
  emptyRow,
  getRowsToImport,
  ImportColumn,
  ImportResult,
  ImportRow,
  MAX_IMPORT_ROWS,
  mergeRows,
  normalizeSerial,
  parsePastedText,
  resultsBySerial,
  summarizeResults,
  validateRows
} from "./deviceImportUtils";
import useStyles from "./DeviceImport.style";

const INITIAL_ROWS = 10;

const initialRows = (): ImportRow[] => Array.from({ length: INITIAL_ROWS }, emptyRow);

const tooManyRows = (count: number) => t`${count} rows found, at most ${MAX_IMPORT_ROWS} devices can be imported at once`;

const statusChip = (label: string, status?: string) => (
  <Chip
    size="small"
    icon={status === "created" ? <CheckCircleIcon /> : <ErrorIcon />}
    label={`${label}: ${status}`}
    color={status === "created" ? "success" : "error"}
  />
);

const DeviceImport = (props: any) => {
  const classes = useStyles();
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);
  const [rows, setRows] = useState<ImportRow[]>(initialRows);
  const [results, setResults] = useState<Record<string, ImportResult>>({});
  const [summary, setSummary] = useState<{ imported: number; failed: number } | null>(null);
  const [importing, setImporting] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);
  const busy = importing || loadingFile;
  const fileInput = useRef<HTMLInputElement>(null);

  const { rowErrors, formError } = validateRows(rows);
  const canImport = !busy && !formError && Object.keys(rowErrors).length === 0;

  const resetOutcome = () => {
    setResults({});
    setSummary(null);
  };

  const updateCell = (rowIndex: number, column: ImportColumn, value: string) => {
    setRows((current) => current.map((row, i) => (i === rowIndex ? { ...row, [column]: value } : row)));
    resetOutcome();
  };

  const handlePaste = (event: React.ClipboardEvent, rowIndex: number, column: ImportColumn) => {
    const pasted = parsePastedText(event.clipboardData.getData("text"));
    // a plain single value is left to the input's default paste
    if (pasted.length === 1 && pasted[0].length === 1) {
      return;
    }
    event.preventDefault();
    if (pasted.length > MAX_IMPORT_ROWS) {
      addMessage({ message: tooManyRows(pasted.length) });
      return;
    }
    setRows((current) =>
      column === "serial" ? mergeRows(current, pasted) : applyPaste(current, rowIndex, column, pasted)
    );
    resetOutcome();
  };

  const removeRow = (rowIndex: number) => {
    setRows((current) => current.filter((_, i) => i !== rowIndex));
    resetOutcome();
  };

  const loadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    setLoadingFile(true);
    try {
      const loaded = await readImportFile(file);
      if (loaded.length > MAX_IMPORT_ROWS) {
        addMessage({ message: tooManyRows(loaded.length) });
        return;
      }
      setRows((current) => mergeRows(current, loaded));
      resetOutcome();
    } catch (error: any) {
      addMessage({ message: error?.message || t`Could not read the file` });
    } finally {
      setLoadingFile(false);
    }
  };

  const clear = () => {
    setRows(initialRows());
    resetOutcome();
  };

  const submit = async () => {
    setImporting(true);
    try {
      const response = await importDevices(getRowsToImport(rows));
      setResults(resultsBySerial(response));
      setSummary(summarizeResults(response));
    } catch (error: any) {
      addMessage({ message: error?.message || t`Import failed` });
    } finally {
      setImporting(false);
    }
  };

  const renderStatus = (row: ImportRow) => {
    const result = results[normalizeSerial(row.serial)];
    if (!result) {
      return null;
    }
    return (
      <>
        {statusChip(t`Pin`, result.pin)} {statusChip(t`Keys`, result.keys)}
        {result.errors?.map((error) => (
          <Typography key={error} variant="caption" display="block" color="error">
            {error}
          </Typography>
        ))}
      </>
    );
  };

  return (
    <ServiceNavigationBar title={t`Import devices`} {...props}>
      <div className={classes.page}>
        <div className={classes.header}>
          <Typography className={classes.title}>{t`Import devices`}</Typography>
          <div className={classes.actions}>
            <input ref={fileInput} type="file" accept=".csv,.xlsx" hidden onChange={loadFile} />
            <Button
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              startIcon={loadingFile ? <CircularProgress size={18} /> : <FileUploadIcon />}
            >
              {t`Load file`}
            </Button>
            <Button onClick={clear} disabled={busy} startIcon={<DeleteSweepIcon />}>
              {t`Clear`}
            </Button>
            <Button
              onClick={() => setRows((current) => [...current, emptyRow()])}
              disabled={busy}
              startIcon={<AddIcon />}
            >
              {t`Add row`}
            </Button>
          </div>
        </div>
        <Typography variant="body2">
          {t`Paste serial and pin columns from a spreadsheet into the grid, or load a .csv / .xlsx file (serial in the first column, pin in the second). Up to ${MAX_IMPORT_ROWS} devices at a time.`}
        </Typography>
        {formError && rows.some((row) => row.serial || row.pin) && <Alert severity="warning">{formError}</Alert>}
        {summary && (
          <Alert severity={summary.failed ? "warning" : "success"}>
            {t`${summary.imported} imported, ${summary.failed} with errors`}
          </Alert>
        )}
        <Paper>
          <TableContainer className={classes.tableWrap}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.indexCell}>#</TableCell>
                  <TableCell>{t`Serial`}</TableCell>
                  <TableCell>{t`Pin`}</TableCell>
                  <TableCell className={classes.statusCell}>{t`Status`}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className={classes.indexCell}>{index + 1}</TableCell>
                    {(["serial", "pin"] as ImportColumn[]).map((column) => (
                      <TableCell key={column}>
                        <TextField
                          size="small"
                          variant="standard"
                          fullWidth
                          value={row[column]}
                          error={!!rowErrors[index]}
                          helperText={column === "pin" ? rowErrors[index] : undefined}
                          disabled={busy}
                          onChange={(event) => updateCell(index, column, event.target.value)}
                          onPaste={(event) => handlePaste(event, index, column)}
                        />
                      </TableCell>
                    ))}
                    <TableCell className={classes.statusCell}>{renderStatus(row)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(index)}
                        disabled={busy}
                        aria-label={t`Remove row`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <div className={classes.footer}>
          <Button
            variant="contained"
            size="large"
            className={classes.importButton}
            onClick={submit}
            disabled={!canImport}
            startIcon={importing ? <CircularProgress size={22} color="inherit" /> : <PublishIcon />}
          >
            {t`Import`}
          </Button>
        </div>
      </div>
    </ServiceNavigationBar>
  );
};

export default DeviceImport;
